import React, { FC, useState, useEffect } from 'react';
import Select from '../../common/components/Select';
import SideBar, {
  Body,
  Footer,
} from '../../common/components/SearchFilters/SideBar';
import { Button, Card, Form } from 'react-bootstrap';
import { Test } from '../../AppointmentCaseManager';

import axios from 'axios';

interface AssignedUser {
  id: number;
  fullName: string;
}

interface CaseStatus {
  value: string;
  label: string;
}

interface CaseReportHistory {
  id?: number;
  caseReportNotes: string;
  assignedUser: AssignedUser;
  caseReportStatus: string;
  tests: Test[];
}

interface LogContactOverlayProps {
  appointmentId: number;
  testGroupSlug: string;
  caseReportHistory: CaseReportHistory;
  caseStatusOptions: CaseStatus[];
  selectableTests: Test[];
  onClose: () => void;
}

interface LogContactIndexProps {
  appointmentId: number;
  testGroupSlug: string;
  currentAdmin: AssignedUser;
  caseReportHistories: CaseReportHistory[];
  caseStatusOptions: CaseStatus[];
  selectableTests: Test[];
}

const newCaseReportHistory = (
  assignedUser: AssignedUser,
): CaseReportHistory => ({
  caseReportNotes: '',
  assignedUser,
  caseReportStatus: '',
  tests: [],
});

const LogContactOverlay: FC<LogContactOverlayProps> = (props) => {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedTime = currentDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
  const [showOverlay, setShowOverlay] = useState(false);
  const [showAllCard, setShowAllCard] = useState(false);
  const [notes, setNotes] = useState(props.caseReportHistory.caseReportNotes);
  const [selectedTests, setSelectedTests] = useState(
    props.caseReportHistory.tests,
  );
  const [selectedCaseStatus, setSelectedCaseStatus] = useState(
    props.caseReportHistory.caseReportStatus,
  );

  useEffect(() => {
    setSelectedTests(props.caseReportHistory.tests);
  }, [props.caseReportHistory.tests]);

  useEffect(() => {
    setNotes(props.caseReportHistory.caseReportNotes);
  }, [props.caseReportHistory.caseReportNotes]);

  useEffect(() => {
    setSelectedCaseStatus(props.caseReportHistory.caseReportStatus);
  }, [props.caseReportHistory.caseReportStatus]);

  const history_params = {
    case_report_history: {
      case_report_status: selectedCaseStatus,
      case_report_notes: notes,
      test_ids: selectedTests.map((test) => test.id),
      admin_id: props.caseReportHistory.assignedUser?.id,
      admin_full_name: props.caseReportHistory.assignedUser?.fullName,
    },
  };

  const handleCreate = () => {
    axios
      .post(
        `/test_groups/${props.testGroupSlug}/appointment_case_reports/${props.appointmentId}/case_report_histories`,
        history_params,
      )
      .then(() => {
        window.location.reload();
      });

    setShowOverlay(false);
    setShowAllCard(false);
  };

  const handleUpdate = () => {
    axios
      .put(
        `/test_groups/${props.testGroupSlug}/appointment_case_reports/${props.appointmentId}/case_report_histories/${props.caseReportHistory.id}`,
        history_params,
      )
      .then(() => {
        window.location.reload();
      });

    setShowOverlay(false);
    setShowAllCard(false);
  };

  const handleDelete = () => {
    axios
      .delete(
        `/test_groups/${props.testGroupSlug}/appointment_case_reports/${props.appointmentId}/case_report_histories/${props.caseReportHistory.id}`,
      )
      .then(() => {
        window.location.reload();
      });

    setShowOverlay(false);
    setShowAllCard(false);
  };

  const isTestSelected = (test: Test) => {
    return selectedTests.some((selectedTest) => selectedTest.id === test.id);
  };

  const handleTestSelection = (test: Test) => {
    if (isTestSelected(test)) {
      setSelectedTests(
        selectedTests.filter((selectedTest) => selectedTest.id !== test.id),
      );
    } else {
      setSelectedTests([...selectedTests, test]);
    }
  };

  return (
    <React.Fragment>
      {props.caseReportHistory.id ? (
        <Card className="mb-3 mt-3">
          <Card.Body>
            <div className="d-flex justify-content-between">
              <strong>{formattedDate}</strong>
              <Button
                variant="link"
                onClick={() => setShowAllCard(!showAllCard)}
              >
                {showAllCard ? 'Hide' : 'Show'}
              </Button>
            </div>
            <p>
              {formattedTime}, {props.caseReportHistory.assignedUser.fullName}
            </p>
            <p>
              {props.caseReportHistory.tests
                .map((test) => test.testConfiguration?.displayName)
                .join(', ')}
            </p>
            {showAllCard && (
              <div>
                <strong>Note</strong>
                <p>{props.caseReportHistory.caseReportNotes}</p>
                <div className="d-flex justify-content-between">
                  <Button
                    variant="link"
                    className="text-danger me-2"
                    onClick={handleDelete}
                  >
                    Remove
                  </Button>
                  <Button
                    className="ms-auto mt-3"
                    style={{ height: 40, paddingInline: 16 }}
                    data-bs-toggle="offcanvas"
                    data-bs-target="#contactSideBar"
                    aria-controls="contactSideBar"
                    aria-label="Toggle navigation"
                    onClick={() => setShowOverlay(true)}
                  >
                    View/Edit
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Button
          className="my-3"
          style={{ height: 40, paddingInline: 16 }}
          data-bs-toggle="offcanvas"
          data-bs-target="#contactSideBar"
          aria-controls="contactSideBar"
          aria-label="Toggle navigation"
          onClick={() => setShowOverlay(true)}
        >
          Log Contact
        </Button>
      )}
      <SideBar
        placement="end"
        show={showOverlay}
        id="contactSideBar"
        style={{ width: '50%' }}
        {...props}
      >
        <Body>
          <div>
            <strong className="my-4" style={{ fontSize: 24 }}>
              Contact
            </strong>
            <div>
              <p>{formattedDate}</p>
              <p>
                {formattedTime}, {props.caseReportHistory.assignedUser.fullName}
              </p>
            </div>
            <div className="my-4">
              <Select
                value={selectedCaseStatus}
                options={props.caseStatusOptions}
                valueKey="value"
                labelKey="label"
                onChange={(value: string) => setSelectedCaseStatus(value)}
                placeholder="Select status"
              />
            </div>
            <strong className="my-4">Test results</strong>
            <br />
            {props.selectableTests.map((test) => (
              <Button
                key={test.id}
                variant={
                  isTestSelected(test) ? 'outline-primary' : 'outline-secondary'
                }
                className="me-2 my-2"
                onClick={() => handleTestSelection(test)}
              >
                {test.testConfiguration?.displayName}
              </Button>
            ))}
            <Form.Group className="my-4">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Form.Group>
          </div>
        </Body>
        <Footer>
          <div className="d-flex justify-content-between">
            <Button
              variant="link"
              className=" text-danger me-2"
              onClick={handleDelete}
            >
              Remove from contact history
            </Button>
            <Button
              variant="primary"
              className="me-2"
              onClick={props.caseReportHistory.id ? handleUpdate : handleCreate}
            >
              Submit
            </Button>
          </div>
        </Footer>
      </SideBar>
    </React.Fragment>
  );
};

const LogContactIndex: FC<LogContactIndexProps> = (props) => {
  return (
    <React.Fragment>
      <LogContactOverlay
        key={'new'}
        appointmentId={props.appointmentId}
        testGroupSlug={props.testGroupSlug}
        caseReportHistory={newCaseReportHistory(props.currentAdmin)}
        caseStatusOptions={props.caseStatusOptions}
        selectableTests={props.selectableTests}
        onClose={() => {}}
      />
      {props.caseReportHistories.map((caseReportHistory) => (
        <LogContactOverlay
          key={caseReportHistory.id}
          appointmentId={props.appointmentId}
          testGroupSlug={props.testGroupSlug}
          caseReportHistory={caseReportHistory}
          caseStatusOptions={props.caseStatusOptions}
          selectableTests={props.selectableTests}
          onClose={() => {}}
        />
      ))}
    </React.Fragment>
  );
};

export default LogContactIndex;
